import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { StyledButton } from '@app/components/button/Button';
import { SelectField } from '@app/components/form-fields/SelectField';
import { TextField } from '@app/components/form-fields/TextField';
import type { RoleOption, User } from '@app/services/api.types';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ModalProps } from 'antd';
import type { MouseEvent } from 'react';
import { useEffect } from 'react';
import type { UseFormReset } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import * as z from 'zod';

const schema = z.object({
    firstName: z.string().trim().min(1, 'First name is required'),
    lastName: z.string().trim().min(1, 'Last name is required'),
    email: z
        .email('Email Address must be valid format')
        .trim()
        .min(1, 'Email Address is required'),
    role: z.string('Role is required'),
});

export type UserFormValues = z.infer<typeof schema>;

export type UserFormProps = ModalProps & {
    data?: User;
    roles?: RoleOption[];
    isEdit?: boolean;
    onSubmit: (
        data: UserFormValues,
        reset: UseFormReset<UserFormValues>
    ) => void;
};

export const UserForm = ({
    roles = [],
    onCancel,
    onSubmit,
    isEdit,
    data,
    ...props
}: UserFormProps) => {
    const {
        handleSubmit,
        control,
        reset,
        formState: { isValid, isSubmitting },
    } = useForm<UserFormValues>({
        mode: 'all',
        resolver: zodResolver(schema),
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            role: undefined,
        },
    });

    useEffect(() => {
        reset(data);
    }, [data, reset]);

    return (
        <form
            onSubmit={handleSubmit((values) => onSubmit(values, reset))}
            id='user-edit-form'
        >
            <div className='form-fields-wrapper'>
                <Controller
                    control={control}
                    name='firstName'
                    render={({ field, fieldState }) => (
                        <TextField
                            fieldState={fieldState}
                            {...field}
                            label='First name'
                            type='text'
                            placeholder='Enter first name'
                        />
                    )}
                />

                <Controller
                    control={control}
                    name='lastName'
                    render={({ field, fieldState }) => (
                        <TextField
                            fieldState={fieldState}
                            {...field}
                            label='Last name'
                            type='text'
                            placeholder='Enter last name'
                        />
                    )}
                />

                <Controller
                    control={control}
                    name='email'
                    render={({ field, fieldState }) => (
                        <TextField
                            fieldState={fieldState}
                            {...field}
                            disabled={isEdit}
                            label='Email'
                            type='email'
                            placeholder='Enter email'
                        />
                    )}
                />

                <Controller
                    control={control}
                    name='role'
                    render={({ field, fieldState }) => (
                        <SelectField
                            fieldState={fieldState}
                            {...field}
                            label='Role'
                            allowClear
                            placeholder='Select role'
                            options={roles.map((role) => ({
                                label: role.name,
                                value: role.id,
                            }))}
                        />
                    )}
                />
            </div>

            <StyledButtonGroup justify='space-between'>
                <StyledButton
                    onClick={(e) => onCancel?.(e as MouseEvent<HTMLButtonElement>)}
                    color='black'
                    data-testid='reset-button'
                >
                    Cancel
                </StyledButton>
                <StyledButton
                    htmlType='submit'
                    type='primary'
                    color='green'
                    disabled={!isValid}
                    loading={isSubmitting || props.confirmLoading}
                    data-testid='register-user-button'
                >
                    Continue
                </StyledButton>
            </StyledButtonGroup>
        </form>
    );
};
